<template>
  <b-overlay :show="_flagShowLoading">
    <div id="app">
      <div
        :class="[
          'layout-d',
          {
            'layout-login': isAuthenticationPage,
            'layout-app': !isAuthenticationPage,
            desktop: desktop && !isAuthenticationPage,
            mobile: !desktop && !isAuthenticationPage
          }
        ]"
      >
        <div :class="[desktop ? 'sticky-menu' : '']">
          <Menu
            v-if="!isAuthenticationPage"
            :desktop="desktop"
            :device="device"
          />
        </div>

        <div class="page">
          <router-view />
          <Footer v-if="!isAuthenticationPage" />
        </div>
      </div>
    </div>

    <template #overlay>
      <div class="text-center d-flex flex-column align-items-center">
        <LoadSpinner class="overlay-svg" />
      </div>
    </template>
  </b-overlay>
</template>

<script>
  import Menu from '@/components/Menu/index.vue';
  import { BOverlay } from 'bootstrap-vue';
  import Footer from '@/components/footer/index.vue';
  import { isUserLoggedIn } from '@/auth/utils';
  import { mapActions, mapGetters, mapMutations } from 'vuex';

  export default {
    name: 'AppRoot',
    components: {
      LoadSpinner: () => import('@/assets/icons/loading.svg'),
      BOverlay,
      Menu,
      Footer
    },
    data: () => {
      return {
        userEmail: '',
        prefix: '',
        desktop: window.innerWidth > 768,
        isAuthenticationPage: true,
        device: 'desktop'
      };
    },
    computed: {
      ...mapGetters('auth', ['getUserInfo', 'getPermissions']),
      _flagShowLoading() {
        return this.$store.state.appStore?.flagShowLoading || false;
      }
    },
    watch: {
      $route(to) {
        this.checkIfIsAuthenticationPage(to);
      }
    },

    beforeCreate() {
      const userInfo = this.$cookies.get('userInfo');
      if (userInfo?.language && this.$i18n) {
        const availableLanguages = Object.keys(this.$i18n.messages);
        if (availableLanguages.includes(userInfo.language)) {
          this.$i18n.locale = userInfo.language;
        }
      }
    },

    async created() {
      this.handleResize();
      window.addEventListener('resize', this.handleResize);

      this.checkIfIsAuthenticationPage(this.$router.currentRoute);

      await this.initializeApp();
    },

    destroyed() {
      window.removeEventListener('resize', this.handleResize);
    },

    methods: {
      ...mapActions('userStore', ['setUser']),
      ...mapActions('auth', ['fetchPermissions']),
      ...mapMutations('appStore', ['UPDATE_PERMISSIONS_MODULES']),
      ...mapMutations('auth', ['SET_USER_INFO']),

      async initializeApp() {
        const cookieUserInfo = this.$cookies.get('userInfo');
        if (cookieUserInfo && cookieUserInfo.userData) {
          const localUserData = JSON.parse(localStorage.getItem('userData') || '{}');
          if (localUserData.guid !== cookieUserInfo.userData.guid) {
            localStorage.setItem('userData', JSON.stringify(cookieUserInfo.userData));
            if (cookieUserInfo.temporaryToken || cookieUserInfo.accessToken) {
              localStorage.setItem('accessToken', cookieUserInfo.temporaryToken || cookieUserInfo.accessToken);
            }
          }
        }

        const isLoggedIn = isUserLoggedIn();

        if (!isLoggedIn && !this.isAuthenticationPage) {
          this.clearStorageNCookies();
          return;
        }

        if (isLoggedIn) {
          try {
            this.setUserStore();
            await this.fetchPermissions();
            this.verifyConnection();
            const domain = window.location.href.includes('indsiders.com');
            this.$cookies.set(
              'modalNoSites',
              { mysfc: false, myconnect: false },
              null,
              null,
              domain ? 'indsiders.com' : null
            );
          } catch (e) {
            console.error('Erro na inicialização:', e);
          }
        }
      },

      verifyConnection() {
        let userInfo = this.getUserInfo || this.$cookies.get('userInfo');
        const company = this.$cookies.get('company');
        const permissions = this.getPermissions;

        if (userInfo) {
          this.UPDATE_PERMISSIONS_MODULES(permissions);

          const userSites = userInfo.userData?.sites || [];
          let siteId = userInfo.currentSite;
          let currentGuid = userSites.find((s) => s.id == siteId || s.guid == siteId);

          if (!currentGuid && userSites.length > 0) {
            siteId = userSites[0].id || userSites[0].guid;
            currentGuid = userSites[0];
          }

          if (currentGuid) {
            userInfo = { ...userInfo, currentSite: siteId, currentGuid };

            this.SET_USER_INFO(userInfo);

            const domain = window.location.href.includes('indsiders.com');
            this.$cookies.set('userInfo', userInfo, null, null, domain ? 'indsiders.com' : null);
          }

          this.userEmail = userInfo.userData.email;
          this.prefix = company ?? '';

          const infoAccount = {
            guid: userInfo.userData.guid,
            employee_id: userInfo.userData.employee_id,
            first_name: userInfo.userData.first_name,
            last_name: userInfo.userData.last_name,
            job_role: userInfo.userData.job_role,
            photo: userInfo.userData.photo
          };

          this.setUser({
            ...userInfo.userData,
            name: `${infoAccount.first_name} ${infoAccount.last_name}`,
            role: infoAccount.job_role,
            picture: infoAccount.photo
          });

          localStorage.setItem('accessToken', userInfo.accessToken);
          localStorage.setItem('userData', JSON.stringify(userInfo.userData));
          localStorage.setItem('prefix', this.prefix);

          this.updateLoggedAccounts(infoAccount);

          this.handleInitialRedirect();
        } else if (!['reset-password', 'reset-pin', 'reset-password-with-prefix', 'reset-pin-with-prefix'].includes(this.$route.name)) {
          this.clearStorageNCookies();
        }
      },

      updateLoggedAccounts(infoAccount) {
        let loggedAccounts = [];
        try {
          loggedAccounts = JSON.parse(localStorage.getItem('loggedAccountsInfo')) || [];
        } catch (e) {
          loggedAccounts = [];
        }

        const index = loggedAccounts.findIndex((item) => item.guid === infoAccount.guid);
        if (index !== -1) loggedAccounts.splice(index, 1);

        loggedAccounts.unshift(infoAccount);
        localStorage.setItem('loggedAccountsInfo', JSON.stringify(loggedAccounts));

        this.loggedAccounts = JSON.stringify(loggedAccounts);
      },

      handleInitialRedirect() {
        const diffMultipleAccount = this.$route.name !== 'multiple-account';
        const pagesToNotRedirect = [
          'reset-password',
          'reset-pin',
          'reset-password-with-prefix',
          'reset-pin-with-prefix',
          'first-access-reset-password',
          'first-access-reset-pin',
          'support',
          'account'
        ];

        if (diffMultipleAccount && !pagesToNotRedirect.includes(this.$route.name)) {
          if (this.$route.name !== 'home') {
            this.$router
              .push({
                name: 'home',
                params: { prefix: this.prefix }
              })
              .catch(() => {});
          }
        }
        if (this._hideLoading) this._hideLoading();
      },

      setUserStore() {
        try {
          const user = JSON.parse(localStorage.getItem('userData'));
          if (user) {
            this.setUser({
              ...user,
              name: `${user.first_name} ${user.last_name}`,
              role: user.job_role,
              picture: user.photo
            });
          }
        } catch (e) {
          // Ignora erro de parse caso o localStorage esteja inválido
        }
      },

      checkIfIsAuthenticationPage(route) {
        const authenticationPages = [
          'login',
          'forgot',
          'reset-password',
          'reset-pin',
          'reset-password-with-prefix',
          'reset-pin-with-prefix',
          'multiple-account',
          'first-access-reset-password',
          'first-access-reset-pin'
        ];
        const targetRoute = route || this.$router.currentRoute;
        this.isAuthenticationPage = authenticationPages.includes(targetRoute.name);
      },

      clearStorageNCookies() {
        localStorage.clear();
        this.UPDATE_PERMISSIONS_MODULES(null);

        const domain = window.location.href.includes('indsiders.com');
        this.$cookies.remove('userInfo', null, domain ? 'indsiders.com' : null);
        this.$cookies.remove('permissions', null, domain ? 'indsiders.com' : null);

        if (this.$route.name !== 'login') {
          this.$router.push('/login').catch(() => {});
        }
      },

      handleResize() {
        this.desktop = window.innerWidth > 768;
        this.device =
          window.innerWidth > 768
            ? 'desktop'
            : window.innerWidth > 425 && window.innerWidth <= 768
            ? 'tablet'
            : 'mobile';
      }
    }
  };
</script>

<style lang="scss">
  @import url('./assets/styles/global.scss');
  @import url('./assets/styles/reset.scss');

  body {
    touch-action: manipulation !important;
    &:has(.full-wide.open) {
      overflow-y: hidden;
      .page {
        display: none !important;
      }
    }
  }

  .layout-app > .page {
    min-height: calc(100dvh - 115px);
    padding-bottom: 67px;
    max-width: 1117px;
    margin: 0 auto;
  }

  .sticky-menu {
    position: sticky;
    top: 0;
    max-height: 100vh;
    z-index: 999;
  }
</style>
